import { useMutation, useQuery } from '@tanstack/vue-query'

import type { ApiResponse } from '@/types/user'

import { useAxios } from '@/composables/use-axios'

/**
 * API配置 - 权限相关接口
 * 基于 api-doc/permission 中的接口文档
 */
const API_BASE_URL_V2_1 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/`

/**
 * 权限配置数据类型
 */
export interface AccessConfig {
  // 页面访问权限
  access_pages?: string[]
  // 渠道分组访问权限（预留）
  access_channel_groups?: string[]
  // 其他权限配置
  [key: string]: any
}

/**
 * 查询权限配置请求
 */
export interface GetAccessRequest {
  user_id: number
}

/**
 * 查询权限配置响应
 */
export interface GetAccessResponse {
  access: AccessConfig
}

/**
 * 保存权限配置请求
 */
export interface SaveAccessRequest {
  user_id: number
  access: AccessConfig
}

/**
 * 保存权限配置响应
 */
export interface SaveAccessResponse {
  code: number
  message: string
  data: null
}

/**
 * 查询权限配置
 * POST /{version}/access/get
 */
export function useAccessGet() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['accessGet'],
    mutationFn: async (data: GetAccessRequest): Promise<GetAccessResponse> => {
      const response = await axiosInstance.post<ApiResponse<GetAccessResponse>>(`${API_BASE_URL_V2_1}access/get`, data)
      return response.data?.data
    },
  })
}

/**
 * 查询权限配置 - Query版本（用于初始化时自动获取）
 * @param userId - 被控制对象的用户ID
 */
export function useAccessGetQuery(userId: number, enabled = true) {
  const { axiosInstance } = useAxios()
  return useQuery({
    queryKey: ['accessConfig', userId],
    queryFn: async (): Promise<GetAccessResponse> => {
      const response = await axiosInstance.post<ApiResponse<GetAccessResponse>>(`${API_BASE_URL_V2_1}access/get`, { user_id: userId })
      return response.data?.data
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新请求
    retry: 1,
  })
}

/**
 * 保存权限配置
 * POST /{version}/access/save
 */
export function useAccessSave() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['accessSave'],
    mutationFn: async (data: SaveAccessRequest): Promise<SaveAccessResponse> => {
      const response = await axiosInstance.post<SaveAccessResponse>(`${API_BASE_URL_V2_1}access/save`, data)
      return response.data
    },
  })
}

/**
 * 直接获取权限配置的函数（非 hook，用于 store 中调用）
 * @param userId - 被控制对象的用户ID
 */
export async function fetchAccessConfig(userId: number): Promise<GetAccessResponse | null> {
  const { axiosInstance } = useAxios()
  try {
    const response = await axiosInstance.post<ApiResponse<GetAccessResponse>>(`${API_BASE_URL_V2_1}access/get`, { user_id: userId })
    return response.data?.data
  }
  catch (error) {
    console.warn('Failed to fetch access config:', error)
    return null
  }
}

/**
 * 直接保存权限配置的函数（非 hook）
 */
export async function saveAccessConfig(data: SaveAccessRequest): Promise<SaveAccessResponse | null> {
  const { axiosInstance } = useAxios()
  try {
    const response = await axiosInstance.post<SaveAccessResponse>(`${API_BASE_URL_V2_1}access/save`, data)
    return response.data
  }
  catch (error) {
    console.error('Failed to save access config:', error)
    throw error
  }
}
